import { forwardRef } from 'react';
import type { LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/base/Button';
import IncreaseFontIcon from '@/icons/IncreaseFont.svg?react';
import ResourceCatalogIcon from '@/icons/ResourceCatalogBlue.svg?react';

/**
 * Document icon component matching LucideIcon signature
 * Used for the User Guide button
 */
const DocumentIcon = forwardRef<SVGSVGElement, LucideProps>(
  ({ className, ...props }, ref) => (
    <svg
      ref={ref}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path d="M5 0H23V22H21V2H5V0Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M1 4H19V24H1V4ZM3 6V22H17V6H3Z" fill="currentColor" />
      <rect x="5" y="9" width="10" height="2" fill="currentColor" />
      <rect x="5" y="13" width="10" height="2" fill="currentColor" />
      <rect x="5" y="17" width="6" height="2" fill="currentColor" />
    </svg>
  )
);
DocumentIcon.displayName = 'DocumentIcon';

export interface TitleBannerControlsContent {
  increaseFontLabel: string;
  submitResourceLabel: string;
  openUserGuideLabel: string;
}

export interface TitleBannerControlsProps {
  content: TitleBannerControlsContent;
  className?: string;
  /**
   * Layout mode for responsive design
   * - 'horizontal' (default): Buttons in a row
   * - 'stacked': Buttons stacked vertically (for tablet layout)
   * - 'mobile': Full-width stacked buttons with large secondary variant
   */
  layout?: 'horizontal' | 'stacked' | 'mobile';
  isFontEnlarged?: boolean;
  onToggleFontSize?: (enlarged: boolean) => void;
  onSubmitResource?: () => void;
  onOpenUserGuide?: () => void;
}

export function TitleBannerControls({
  content,
  className,
  layout = 'horizontal',
  isFontEnlarged = false,
  onToggleFontSize,
  onSubmitResource,
  onOpenUserGuide,
}: TitleBannerControlsProps) {
  const handleIncreaseFontClick = () => {
    onToggleFontSize?.(!isFontEnlarged);
  };

  // Icon size classes based on font enlargement state
  const iconSizeClass = isFontEnlarged ? 'w-7 h-7' : 'w-6 h-6';

  // Font size style override when enlarged (+4px: 16px → 20px)
  const enlargedFontStyle = isFontEnlarged ? { fontSize: '20px' } : undefined;

  // Mobile layout: Full-width stacked buttons with large secondary variant
  if (layout === 'mobile') {
    return (
      <div className={cn('flex flex-col gap-2 w-full', className)}>
        {/* Increase Font Button */}
        <Button
          type="button"
          variant={isFontEnlarged ? 'oh-primary' : 'oh-secondary'}
          onClick={handleIncreaseFontClick}
          className={cn(
            'w-full px-4 justify-center',
            isFontEnlarged ? 'h-auto py-3' : 'h-12'
          )}
          style={enlargedFontStyle}
        >
          <span>{content.increaseFontLabel}</span>
          <IncreaseFontIcon className={cn(iconSizeClass, isFontEnlarged ? 'text-white' : 'text-[#0A4D8F]')} aria-hidden="true" />
        </Button>

        {/* Submit a Resource Button */}
        <Button
          type="button"
          variant="oh-secondary"
          onClick={onSubmitResource}
          className={cn(
            'w-full px-4 justify-center',
            isFontEnlarged ? 'h-auto py-3' : 'h-12'
          )}
          style={enlargedFontStyle}
        >
          <span>{content.submitResourceLabel}</span>
          <ResourceCatalogIcon className={iconSizeClass} aria-hidden="true" />
        </Button>

        {/* Open User Guide Button */}
        <Button
          type="button"
          variant="oh-secondary"
          onClick={onOpenUserGuide}
          className={cn(
            'w-full px-4 justify-center',
            isFontEnlarged ? 'h-auto py-3' : 'h-12'
          )}
          style={enlargedFontStyle}
        >
          <span>{content.openUserGuideLabel}</span>
          <DocumentIcon className={cn(iconSizeClass, 'text-[#0A4D8F]')} aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex gap-2',
      layout === 'horizontal' ? 'items-center flex-row' : 'flex-col items-start',
      className
    )}>
      {/* Increase Font Button - toggles between secondary and primary styling */}
      <Button
        type="button"
        variant={isFontEnlarged ? 'oh-primary' : 'oh-secondary'}
        onClick={handleIncreaseFontClick}
        className={cn(
          'w-auto px-4',
          isFontEnlarged ? 'h-auto py-2' : 'h-[40px]'
        )}
        style={enlargedFontStyle}
      >
        <span>{content.increaseFontLabel}</span>
        <IncreaseFontIcon className={cn(iconSizeClass, isFontEnlarged ? 'text-white' : 'text-[#0A4D8F]')} aria-hidden="true" />
      </Button>

      {/* Submit a Resource Button - always secondary styling */}
      <Button
        type="button"
        variant="oh-secondary"
        onClick={onSubmitResource}
        className={cn(
          'w-auto px-4',
          isFontEnlarged ? 'h-auto py-2' : 'h-[40px]'
        )}
        style={enlargedFontStyle}
      >
        <span>{content.submitResourceLabel}</span>
        <ResourceCatalogIcon className={iconSizeClass} aria-hidden="true" />
      </Button>

      {/* Open User Guide Button - secondary styling */}
      <Button
        type="button"
        variant="oh-secondary"
        onClick={onOpenUserGuide}
        className={cn(
          'w-auto px-4',
          isFontEnlarged ? 'h-auto py-2' : 'h-[40px]'
        )}
        style={enlargedFontStyle}
      >
        <span>{content.openUserGuideLabel}</span>
        <DocumentIcon className={cn(iconSizeClass, 'text-[#0A4D8F]')} aria-hidden="true" />
      </Button>
    </div>
  );
}

export default TitleBannerControls;
